import clsx from "clsx";
import * as yup from "yup";
import { FormattedMessage, useIntl } from "react-intl";
import { Form, Formik } from "formik";
import {
  Box,
  Divider,
  FormControlLabel,
  Button,
  CircularProgress,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  createTheme,
  ThemeProvider,
} from "@material-ui/core";
import { useStyles } from "../../../shared/styles/useStyles";
import { MyTextField } from "../../../components/custom/MyTextField";
import { IParamResponseTime } from "../../../redux/types/types";
import { TypeDays } from "../../../enums/enums";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../redux/reducers/rootReducer";
import { updateResponseTimesForm } from "../../../redux/actions/correspondenceActions";
import { MyAlert } from "../../../components/custom/MyAlert";
import {
  setSuccessMsg,
  uiCloseErrorAlert,
  uiCloseSuccessAlert,
} from "../../../redux/actions/uiActions";

const theme = createTheme({
  typography: {
    fontFamily: "Poppins",
    fontSize: 16,
  },
});

export const ResponseTimesTab = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useDispatch();

  const { paramResponseTime } = useSelector(
    (state: AppState) => state.correspondence
  );

  const { successAlert, errorAlert, successMsg, errorMsg } = useSelector(
    (state: AppState) => state.ui
  );

  const onCloseSuccessAlert = () => {
    dispatch(uiCloseSuccessAlert());
    dispatch(setSuccessMsg(null));
  };

  const validationSchema = yup.object({
    RightToPetitionInterestDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    RightToPetitionInterestTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    CitizenComplaintsDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    CitizenComplaintsTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    PublicServantComplaintsDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    PublicServantComplaintsTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    ClaimsDays: yup
      .number()
      .min(1)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ClaimsTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    ComplaintsDays: yup
      .number()
      .min(1)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ComplaintsTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ApplicationDocumentsOrCopiesDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ApplicationDocumentsOrCopiesTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    ApplicationPublicInformationDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ApplicationPublicInformationTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    PetitionBetweenAuthoritiesDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    PetitionBetweenAuthoritiesTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    ConsultationApplicationDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ConsultationApplicationTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    ConceptApplicationDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ConceptApplicationTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    CopyByCompetitionDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    CopyByCompetitionTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),

    ApplicationDays: yup
      .number()
      .min(1, `${intl.formatMessage({ id: "PositiveNumber" })}`)
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
    ApplicationTypeDays: yup
      .string()
      .required(`${intl.formatMessage({ id: "RequiredFile" })}`),
  });

  let initialValues: IParamResponseTime = {
    RightToPetitionInterestDays: paramResponseTime.RightToPetitionInterestDays,
    RightToPetitionInterestTypeDays:
      paramResponseTime.RightToPetitionInterestTypeDays,
    CitizenComplaintsDays: paramResponseTime.CitizenComplaintsDays,
    CitizenComplaintsTypeDays: paramResponseTime.CitizenComplaintsTypeDays,
    PublicServantComplaintsDays: paramResponseTime.PublicServantComplaintsDays,
    PublicServantComplaintsTypeDays:
      paramResponseTime.PublicServantComplaintsTypeDays,
    ClaimsDays: paramResponseTime.ClaimsDays,
    ClaimsTypeDays: paramResponseTime.ClaimsTypeDays,
    ComplaintsDays: paramResponseTime.ComplaintsDays,
    ComplaintsTypeDays: paramResponseTime.ComplaintsTypeDays,
    ApplicationDocumentsOrCopiesDays:
      paramResponseTime.ApplicationDocumentsOrCopiesDays,
    ApplicationDocumentsOrCopiesTypeDays:
      paramResponseTime.ApplicationDocumentsOrCopiesTypeDays,
    ApplicationPublicInformationDays:
      paramResponseTime.ApplicationPublicInformationDays,
    ApplicationPublicInformationTypeDays:
      paramResponseTime.ApplicationPublicInformationTypeDays,
    PetitionBetweenAuthoritiesDays:
      paramResponseTime.PetitionBetweenAuthoritiesDays,
    PetitionBetweenAuthoritiesTypeDays:
      paramResponseTime.PetitionBetweenAuthoritiesTypeDays,
    ConsultationApplicationDays: paramResponseTime.ConsultationApplicationDays,
    ConsultationApplicationTypeDays:
      paramResponseTime.ConsultationApplicationTypeDays,
    ConceptApplicationDays: paramResponseTime.ConceptApplicationDays,
    ConceptApplicationTypeDays: paramResponseTime.ConceptApplicationTypeDays,
    CopyByCompetitionDays: paramResponseTime.CopyByCompetitionDays,
    CopyByCompetitionTypeDays: paramResponseTime.CopyByCompetitionTypeDays,
    ApplicationDays: paramResponseTime.ApplicationDays,
    ApplicationTypeDays: paramResponseTime.ApplicationTypeDays,
  };



  return (
    <ThemeProvider theme={theme}>
      <Divider variant="fullWidth" />
      <Paper square className={classes.paper}>
        <Box m={1} ml={10}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (data, { setSubmitting }) => {
              setSubmitting(true);
              await dispatch(updateResponseTimesForm(data));
              setSubmitting(false);
            }}
          >
            {({ isSubmitting, values, handleChange }) => (
              <Form className={classes.input}>
                <br />
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <h1 className={classes.title}>
                      <FormattedMessage id="TypeCorrespondence" />
                    </h1>
                  </Grid>

                  <Grid item xs={4} className={classes.title}>
                    <h1 className={classes.title}>
                      <FormattedMessage id="DaysM" />
                    </h1>
                  </Grid>

                  <Grid item xs={4}></Grid>

                  {/* //////////////////////////  1  derecho de petición  ///////////////////////////////// */}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="RightToPetitionInterest" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="RightToPetitionInterestDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="RightToPetitionInterestTypeDays"
                        value={values.RightToPetitionInterestTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {/* //////////////////////////  2  quejas  ///////////////////////////////// */}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="CitizenComplaints" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="CitizenComplaintsDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="CitizenComplaintsTypeDays"
                        value={values.CitizenComplaintsTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {/* //////////////////////////  3 reclamos  ///////////////////////////////// */}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="PublicServantComplaints" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="PublicServantComplaintsDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="PublicServantComplaintsTypeDays"
                        value={values.PublicServantComplaintsTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {/* //////////////////////////  4 sugerencias  ///////////////////////////////// */}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="Claims" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="ClaimsDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="ClaimsTypeDays"
                        value={values.ClaimsTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {/* //////////////////////////  5 tutelas  ///////////////////////////////// */}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="Complaints" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="ComplaintsDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="ComplaintsTypeDays"
                        value={values.ComplaintsTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {/* //////////////////////////  7 consultas  ///////////////////////////////// */}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="ApplicationDocumentsOrCopies" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="ApplicationDocumentsOrCopiesDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="ApplicationDocumentsOrCopiesTypeDays"
                        value={values.ApplicationDocumentsOrCopiesTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                  {/*////////////////////*/}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="ApplicationPublicInformation" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="ApplicationPublicInformationDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="ApplicationPublicInformationTypeDays"
                        value={values.ApplicationPublicInformationTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {/*////////////////////*/}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="PetitionBetweenAuthorities" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="PetitionBetweenAuthoritiesDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="PetitionBetweenAuthoritiesTypeDays"
                        value={values.PetitionBetweenAuthoritiesTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>

                  {/*////////////////////*/}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="ConsultationApplication" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="ConsultationApplicationDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="ConsultationApplicationTypeDays"
                        value={values.ConsultationApplicationTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                  {/*////////////////////*/}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="ConceptApplication" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="ConceptApplicationDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="ConceptApplicationTypeDays"
                        value={values.ConceptApplicationTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                  {/*////////////////////*/}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="CopyByCompetition" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="CopyByCompetitionDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="CopyByCompetitionTypeDays"
                        value={values.CopyByCompetitionTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                  {/*////////////////////*/}

                  <Grid item xs={4} className={classes.titleCenter}>
                    <FormattedMessage id="Application" />
                  </Grid>

                  <Grid item xs={1}>
                    <MyTextField
                      name="ApplicationDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      className={classes.myTextFieldRoot}
                    ></MyTextField>
                  </Grid>

                  <Grid item xs={5} className={classes.botton_between}>
                    <Box ml={5}>
                      <RadioGroup
                        className={classes.botton_between}
                        row
                        aria-label="position"
                        name="ApplicationTypeDays"
                        value={values.ApplicationTypeDays}
                        onChange={handleChange}
                      >
                        <FormControlLabel
                          value={TypeDays.BUSINESSDAYS}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({
                            id: "BusinessDays",
                          })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                        <FormControlLabel
                          value={TypeDays.CALENDAR}
                          control={<Radio color="primary" />}
                          label={`${intl.formatMessage({ id: "Calendar" })}`}
                          classes={{
                            label: classes.checkboxLabel,
                          }}
                        />
                      </RadioGroup>
                    </Box>
                  </Grid>
                </Grid>

                <Box
                  mt={5}
                  ml={30}
                  display="flex"
                  alignItems="center"
                  flexDirection="column-reverse"
                >
                  {!isSubmitting ? (
                    <Button
                      className={clsx(classes.btn, classes.save)}
                      autoFocus
                      type="submit"
                      disabled={isSubmitting}
                    >
                      <FormattedMessage id="Save" />
                    </Button>
                  ) : (
                    <Button
                      className={clsx(classes.btn, classes.save)}
                      autoFocus
                      type="button"
                      disabled={true}
                    >
                      <CircularProgress className={classes.btnLoading} />
                    </Button>
                  )}
                </Box>

                <br />
                {successMsg && (
                  <MyAlert
                    open={successAlert}
                    typeAlert="success"
                    message={successMsg}
                    time={2000}
                    handleClose={onCloseSuccessAlert}
                  />
                )}
                {errorMsg && (
                  <MyAlert
                    open={errorAlert}
                    typeAlert="error"
                    message={errorMsg}
                    time={3000}
                    handleClose={() => dispatch(uiCloseErrorAlert())}
                  />
                )}
              </Form>
            )}
          </Formik>
        </Box>
      </Paper>
    </ThemeProvider>
  );
};
